import { globalConfig, globalTools, modelDialogTools } from '@/utils/globalConfig';
import { sceneUtils } from '@/utils/sceneUtils';
import { VisionaryScene } from '@/visionaryControllers/visionaryScene.ts';
import { getI18nText } from '@/utils/commonFunc';

export class FileController {
  private fileInput: HTMLInputElement | null = null;
  private sceneInput: HTMLInputElement | null = null;
  private statusDiv: HTMLDivElement | null = null;
  private visionaryScene: VisionaryScene;

  constructor(scene: VisionaryScene) {
    this.visionaryScene = scene;
    this.ensureModelFileInput();
  }

  /** 提前挂好隐藏 input，避免首次点击再在关键路径上 createElement/appendChild */
  private ensureModelFileInput() {
    if (this.fileInput) return;
    const inputdom = document.createElement('input');
    inputdom.id = 'modelFileInput';
    inputdom.type = 'file';
    inputdom.accept = '.onnx,.ply,.glb,.gltf,.spz';
    inputdom.multiple = true;
    inputdom.addEventListener('change', () => { void this.onFileSelected(); });
    inputdom.style.display = 'none';
    document.body.appendChild(inputdom);
    this.fileInput = inputdom;
  }

  private async loadModelByFile(file: File, options: any = {}) {
    let ext = file.name.toLowerCase().split('.').pop();
    let isGaussian = ext === 'onnx';

    // if (ext === 'ply') {
    //   isGaussian = await this.is3dgsPly(file);
    // }
    if (!options) {
      options = {
        onProgress: (progress: number) => { modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingFile', { fileName: file.name, progress: Math.round(progress * 100) })); }
      }
    }
    options.isGaussian = isGaussian; // 是否高斯模型的判断逻辑放进core中，对外只使用loadModel
    try {
      let result = await this.visionaryScene.loadModel(file, options);
      return result;
    } catch (error) {
      console.error(`加载文件 ${file.name} 失败:`, error);
      throw error;
    }
  }

  // public openSceneFolderDialog() {
  //   if (!this.sceneInput) {
  //     let inputdom = document.createElement('input');
  //     inputdom.id = 'sceneFileInput';
  //     // Use file input with directory selection attributes for folder picking
  //     inputdom.type = 'file';
  //     inputdom.setAttribute('webkitdirectory', '');
  //     inputdom.setAttribute('directory', '');
  //     inputdom.setAttribute('mozdirectory', '');
  //     inputdom.multiple = true;
  //     inputdom.addEventListener('change', () => this.onSceneFolderSelected())
  //     inputdom.style.display = 'none';
  //     document.body.appendChild(inputdom);
  //     this.sceneInput = inputdom;
  //   }

  //   this.sceneInput!.click();
  // }

  public openMarbleModelListDialog() {
    console.log('openMarbleModelListDialog');
    modelDialogTools.openMarbleModelListDialog(this.visionaryScene);
  }

  public openModelFileDialog() {
    this.ensureModelFileInput();
    this.fileInput!.click();
  }

  // 打开Marble模型文件选择对话框 
  public async openMarbleModelFileDialog() {
    modelDialogTools.openMarbleImportDialog();
  }

  private onSceneFolderSelected() {
    const input = this.sceneInput as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) {
      console.log('未选择文件夹');
      input.value = '';
      return;
    }

    // Derive root folder from the first file's webkitRelativePath
    const firstFile: any = files[0] as any;
    const relPath: string = (firstFile && firstFile.webkitRelativePath) || firstFile.name || '';
    let rootFolder = '';
    if (relPath && relPath.includes('/')) {
      rootFolder = relPath.split('/')[0];
    } else {
      // Fallback to filename from input value
      rootFolder = input.value.replace(/.*[\\\/]/, '');
    }

    console.log('场景文件夹选择', rootFolder, files);
    this.updateStatus(`已选择文件夹: ${rootFolder}（${files.length} 项）`);
  }

  private async onFileSelected() {
    const files = this.fileInput?.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      console.log('文件选择', fileArray);
      try {
        await this.loadModels(fileArray);
      } finally {
        // 清空 input 会作废 File 句柄。必须等读取结束后再清，否则大文件 slice() 会 NotReadableError。
        if (this.fileInput) this.fileInput.value = '';
      }
    } else {
      console.log('没有文件选择');
      if (this.fileInput) this.fileInput.value = '';
    }
  }

  /**
   * 加载模型文件
   * 
   */
  public async loadModels(files: File[]) {
    try {
      modelDialogTools.showLoadingInfoModal('正在加载模型...');

      // 使用新的统一加载方法
      const fileArray = Array.from(files);

      // 分别处理不同类型的文件
      const results = [];
      let loadedCount = 0;

      for (const file of fileArray) {
        try {
          const result = await this.loadModelByFile(file, {
            onProgress: (progress: number) => {
              modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingFile', { fileName: file.name, progress: Math.round(progress * 100) }));
            }
          });

          results.push(result);

          loadedCount++;
        } catch (error) {
          console.error(`加载文件 ${file.name} 失败:`, error);
          modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.fileLoadFailed', { fileName: file.name, error: (error as Error).message }));
        }
      }

      modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.modelsLoadSuccess', { loadedCount, totalCount: fileArray.length }));
      console.log('模型加载完成:', results);

    } catch (error) {
      console.error('加载模型失败:', error);
      modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadFailed', { error: (error as Error).message }));
    } finally {
      sceneUtils.setModelAnimation(globalConfig.sceneParams.isAnimation); // 统一设置动画预览状态，同步加载的模型，避免加载模型时动画状态不一致
      modelDialogTools.closeLoadingInfoModal(1);
    }
  }

  /**
   * 获取模型列表
   * @returns 模型列表
   */
  public getModelList() {
    return this.visionaryScene.getObjects();
  }

  /**
   * 更新加载状态 -- 需要考虑是否做一个公共方法，用于更新所有加载状态 by helen 2025-10-28
   * @param message 状态消息
   */
  private updateStatus(message: string) {
    if (!this.statusDiv) {
      this.statusDiv = document.createElement('div');
      this.statusDiv.id = 'modelStatus';
      document.body.appendChild(this.statusDiv);
    }
    this.statusDiv.textContent = message;
    console.log('更新加载状态----', message);
  }

  public destroy() {
    if (this.fileInput) {
      this.fileInput.remove();
      this.fileInput = null;
    }
    if (this.statusDiv) {
      this.statusDiv.remove();
      this.statusDiv = null;
    }
  }

  // private async readFileHeader(file: File, maxBytes = 4096): Promise<string> {
  //   const slice = file.slice(0, maxBytes);
  //   const arrayBuffer = await slice.arrayBuffer();
  //   return new TextDecoder('utf-8').decode(arrayBuffer || new ArrayBuffer(0));
  // }

  // private async is3dgsPly(file: File): Promise<boolean> {
  //   try {
  //     const header = (await this.readFileHeader(file)).toLowerCase();
  //     if (!header.startsWith('ply')) {
  //       console.log('不是 PLY 文件:', file.name);
  //       return false;
  //     }

  //     const requiredKeywords = [
  //       'property float opacity',
  //       'property float scale_0',
  //       'property float scale_1',
  //       'property float scale_2',
  //       'property float rot_0',
  //       'property float rot_1',
  //       'property float rot_2',
  //       'property float rot_3'
  //     ];

  //     const hasAllBaseProps = requiredKeywords.every(keyword => header.includes(keyword));
  //     const hasShCoefficients = /property\s+float\s+sh_\d+/.test(header);
  //     console.log(`PLY 文件 ${file.name} 3DGS 检测结果: 基础属性=${hasAllBaseProps}, SH 系数=${hasShCoefficients}`);

  //     return hasAllBaseProps;
  //   } catch (error) {
  //     console.warn('读取 PLY 头信息失败，按非 3DGS 处理:', file.name, error);
  //     return false;
  //   }
  // }

}

